import moment from 'moment';
import store from 'redux/store';
import { IChangeFixedPlan } from 'utils/api/queries/omantelApi';
import { OMANNET_PREFFERRED, SMARTPAY } from './constant';

export interface AccountDetails {
	accountId: string;
	governorate: string;
	region: string;
	city: string;
	way: string;
	house: string;
	apartment: string;
	landmark: string;
}

export function updateCorpAddress(accountDetails: AccountDetails) {
	return accountDetails;
}

export function requestResetPasswordOtp(login: string) {
	return { sendOtp: 'true', language: 'EN', login, corpUser: 'true' };
}

export interface AccountInfo {
	phoneNumber: string;
	accountNumber?: string;
	type?: string[];
	accountType?: string; // mobile/fixed-line/internet etc.
	billingType?: string; // postpaid etc.
	startPeriod?: string;
	endPeriod?: string;
	resourceInfo?: boolean;
	noCache?: boolean;
}

const transactionCodes = {
	APR: 'A',
	TOP_UP: 'T',
	LOYALTY_SUBSCRIPTION: 'LS',
	PAY: 'P',
	GROUP_TOP_UP: 'G',
	ESHOP: 'S',
	DATA: 'D',
	VOUCHER: 'V',
};

export function getProducts(accountInfo: AccountInfo) {
	return {
		customerAccount: {
			product: {
				productCharacteristic: {
					name: 'PHONE_NUMBER',
					value: accountInfo.phoneNumber,
				},
				productSpecification: {
					productSpecCharacteristic: {
						name: 'BILLING_TYPE',
						productSpecCharacteristicValue: { value: accountInfo.billingType },
					},
					productCategory: [{ type: accountInfo.accountType }],
				},
			},
		},
	};
}

export interface ProductOrderInfo {
	billingType: string;
	newSimNumber: string;
	oldSimNumber: string;
	newActivationDate: string;
	oldActivationDate: string;
	phoneNumber: string;
	accountNumber: string;
	accountExternalId: string;
	subscriberNumber: string;
	otp: string;
}

export function createProductOrder(productOrderInfo: ProductOrderInfo) {
	return {
		productOrder: {
			otp: productOrderInfo?.otp,
			orderItems: [
				{
					action: 'ADD',
					resource: {
						resourceSpecification: {
							name: 'SIM',
						},
						resourceCharacteristicValue: [
							{
								name: `PRIMARY_SIM_${productOrderInfo.billingType.toUpperCase()}`,
								value: productOrderInfo.newSimNumber,
							},
							{
								name: 'ACTIVE_DATE',
								value: productOrderInfo.newActivationDate,
							},
						],
					},
				},
				{
					action: 'REMOVE',
					resource: {
						resourceSpecification: {
							name: 'SIM',
						},
						resourceCharacteristicValue: [
							{
								name: `PRIMARY_SIM_${productOrderInfo.billingType.toUpperCase()}`,
								value: productOrderInfo.oldSimNumber,
							},
							{
								name: 'ACTIVE_DATE',
								value: productOrderInfo.oldActivationDate,
							},
							{
								name: 'EXTERNAL_ID',
								value: productOrderInfo.phoneNumber,
							},
						],
					},
				},
			],
			relatedParties: {
				customerAccount: {
					id: productOrderInfo.accountExternalId,
					product: {
						productCharacteristic: [
							{
								name: 'PHONE_NUMBER',
								value: productOrderInfo.phoneNumber,
							},
							{
								name: 'ACCOUNT_NO',
								value: productOrderInfo.accountNumber,
							},
							{
								name: 'ACCOUNT_EXTERNAL_ID',
								value: productOrderInfo.accountExternalId,
							},
							{
								name: 'SERVICE_TYPE',
								value: '1',
							},
							{
								name: 'SUBSCRIBER_NUMBER',
								value: productOrderInfo.subscriberNumber,
							},
							{
								name: 'SUBSCRIBER_NUMBER_RESETS',
								value: '0',
							},
						],
					},
				},
			},
		},
	};
}

export interface CheckResourceAvailable {
	resourceType: string;
	serialNo: string;
}

export function checkResourceAvailable(data: CheckResourceAvailable) {
	return { ...data };
}

export interface CreditLimitInfo {
	limit: string;
	existingLimit: string;
	phoneNumber: string;
	accountNumber: string;
	accountId: string;
	documentId?: string | undefined;
	otp?: string;
}

export function changeCreditLimit(creditLimitInfo: CreditLimitInfo) {
	return {
		productOrder: {
			type: 'EBU',
			id: creditLimitInfo.accountId,
			otp: creditLimitInfo.otp,
			orderItems: {
				action: 'MODIFY',
				product: {
					productCharacteristic: [
						{ name: 'ACCOUNT_NUMBER', value: creditLimitInfo.accountNumber },
						{ name: 'PHONE_NUMBER', value: creditLimitInfo.phoneNumber },
						{ name: 'EXISTING_CREDIT_LIMIT', value: creditLimitInfo.existingLimit },
						{ name: 'DOCUMENT_ID', value: creditLimitInfo.documentId },
						{
							name: 'CREDIT_LIMIT',
							value: creditLimitInfo.limit,
							action: 'MODIFY',
						},
					],
					productSpecification: {
						productCategory: { type: 'CORPORATE_CREDIT_LIMIT' },
					},
				},
			},
		},
	};
}

export function getCreditLimit(accountInfo: AccountInfo) {
	return {
		customerAccount: {
			product: {
				productCharacteristic: [
					{
						name: 'PHONE_NUMBER',
						value: accountInfo.phoneNumber,
					},
					{
						name: 'ACCOUNT_NUMBER',
						value: accountInfo.accountNumber,
					},
				],
				productSpecification: {
					productCategory: {
						type: 'CORPORATE_CREDIT_LIMIT',
					},
				},
			},
		},
	};
}

export function getUsage(accountInfo: AccountInfo) {
	return {
		accountType: accountInfo.accountType,
		phoneNumber: accountInfo.phoneNumber,
		includePackages: true,
		getProducts: true,
		billingType: accountInfo.billingType,
		language: 'EN',
		apiVersion: 3,
	};
}

export function getCustomerAccountBills() {
	return {
		corpUser: true,
	};
}

// export function getRecommendations() {
//   return {
//     recommendationType: "Promotion",
//     language: "EN",
//     accountNumber: "67856915"
//   };
// }

// export function getPhotoForCurrentUser() {
//   return {
//     thumbnail: "false"
//   };
// }

export function getNumbersForBillingAccount(accountId: string, billingId: string) {
	return {
		accountId: accountId,
		billingId: billingId,
	};
}

export function getCustomerAccountOperation(accountInfo: AccountInfo) {
	const data = {
		customer: {
			customerAccount: {
				product: {
					productCharacteristic: [
						{
							name: 'PHONE_NUMBER',
							value: accountInfo.phoneNumber,
						},
					],
					productSpecification: {
						productCategory: { type: accountInfo.accountType },
					},
				},
			},
		},
	};

	if (accountInfo?.noCache) {
		data.customer.customerAccount.product.productCharacteristic.push({
			name: 'NO_CACHE',
			value: 'true',
		});
	}

	return data;
}

export function getNumberUsage(accountInfo: AccountInfo) {
	return {
		usage: {
			type: accountInfo.type,
			range: {
				endPeriod: accountInfo.endPeriod,
				startPeriod: accountInfo.startPeriod,
			},
			usageCharacteristic: [
				{
					name: 'PHONE_NUMBER',
					value: accountInfo.phoneNumber,
				},
				{
					name: 'ACCOUNT_NUMBER',
					value: accountInfo.accountNumber,
				},
				{
					name: 'PRODUCT_CATEGORY',
					value: accountInfo.accountType,
				},
				{
					name: 'BILLING_TYPE',
					value: accountInfo.billingType,
				},
			],
		},
	};
}

export interface AccountData {
	description: string;
	id: string;
}

export function updateAccountNameForLoggedUser(accountData: AccountData) {
	return accountData;
}

export function getCustomerProducts(accountInfo: AccountInfo) {
	return {
		phoneNumber: accountInfo.phoneNumber,
		billingType: accountInfo.billingType,
		accountType: accountInfo.accountType,
		resourceInfo: accountInfo.resourceInfo,
	};
}

export interface CustomerBillInfo {
	customerAccountId: string;
	startDate: string;
	attachmentType: 'PDF' | 'URL';
}

export function getCustomerBills(customerBillInfo: CustomerBillInfo) {
	return {
		customer: {
			customerAccount: {
				id: customerBillInfo.customerAccountId,
			},
		},
		attachment: {
			type: customerBillInfo.attachmentType,
		},
		startDateTime: customerBillInfo.startDate,
	};
}

export interface UserInfo {
	loginOrEmail: string;
	last4DocumentDigits: string;
}

export function initializeResetPassword(userInfo: UserInfo) {
	return { ...userInfo, corpUser: true };
}

export function getCorpUsersForNumber(upAccountId: string) {
	return { upAccountId };
}

export interface UserNumbers {
	userId: string;
	numbers: string[];
}

export function assignNumbersForCorpUserRestPost(userNumbers: UserNumbers) {
	return userNumbers;
}

export function getCorpUserDepartmentDataForNumber(upAccountId: string) {
	return { upAccountId };
}

export interface DepartmentData {
	name: string;
	description?: string;
	uiColor: string;
	accountId?: string;
	departmentId?: string;
}

export function addDepartment(departmentData: DepartmentData) {
	return departmentData;
}

export function getDepartmentDetails(departmentId: string, accountId: string) {
	return { departmentId, accountId };
}

export function getCorpAccountDetails(accountId: string) {
	return { accountId };
}

export function updateDepartment(departmentData: DepartmentData) {
	return departmentData;
}

export function deleteDepartment(departmentId: string) {
	return { departmentId };
}

export function getCorpAccountPhoto(accountId: string) {
	return { accountId, thumbnail: true };
}

export interface PhotoData {
	accountId: string;
	base64photo: string;
}

export function setCorpAccountPhoto(photoData: PhotoData) {
	return { accountId: photoData.accountId, base64photo: photoData.base64photo };
}

export function getNumbersForAccount(accountId: string, unassignedOnly?: boolean) {
	return { accountId, unassignedOnly };
}

export interface DepartmentNumbers {
	departmentId: string;
	numbers: string[];
}

export function assignNumbersToDepartment(departmentNumbers: DepartmentNumbers) {
	return departmentNumbers;
}

export function getCorpAccountAdmins(accountId: string) {
	return { accountId };
}

export function getCorpAccountManagers(accountId: string) {
	return { accountId };
}

export interface AccountContactData {
	accountId: string;
	email?: string;
	contactNumber?: string;
	contactNumber2?: string;
	contactNumber3?: string;
}

export function updateCorpAccountContact(accountContactData: AccountContactData) {
	return accountContactData;
}

export function getManagersForAccounts(departmentId: string, accountId: string) {
	return { departmentId, accountId };
}

export interface AssignManagersToDepartmentData {
	departmentId: string;
	userIds: string[];
	permissionExpiration?: string;
}

export function assignManagersToDepartment(data: AssignManagersToDepartmentData) {
	return data;
}

export interface DeleteManagersFromDepartmentData {
	departmentId: string;
	userIds: string[];
}

export function deleteManagersFromDepartment(data: DeleteManagersFromDepartmentData) {
	return data;
}

export function makePayment({
	type,
	items,
	paymentToken,
	billingAddress: address,
	card,
	totalPrice,
	createPaymentToken,
	tempPaymentEmail,
	subscriptionId,
	quantity,
	createDefaultPayment,
}: types.payment.MakePayment) {
	const state = store.getState();
	const DISABLE_SMART_PAY = !state?.settings?.remoteConfig?.sidebar?.smartPayment;
	const ENABLE_BANK_MUSCAT = state?.settings?.remoteConfig?.sidebar?.bankMuscat;
	const flowType = DISABLE_SMART_PAY ? 'OMANNET_PREFFERRED' : 'SMARTPAY';

	const payload: any = {
		amount: items.length,
		channel: 'EBU',
		name: null,
		flowType,
		subscriptionId,
		transactionCode: transactionCodes[type],
		details: {
			item: items.map((item) => ({
				...item,
				accountId: item.accountId || null,
				accountType: item.accountType || null,
				name: item.name || null,
				quantity: quantity?.toString(),
			})),
		},
		...(ENABLE_BANK_MUSCAT && { createDefaultPayment: createDefaultPayment }),
	};

	if (!!address && !address.email && tempPaymentEmail) {
		address.email = tempPaymentEmail;
	}

	if (paymentToken) {
		payload.useToken = paymentToken;
	} else if (!!address && !!card) {
		payload.billingAddress = address;
		payload.card = card;
		/* this condition is specifically for amex card */
		payload.card.type = card.type.replace('-', '_');
		payload.createPaymentToken = createPaymentToken;
	}

	if (type === 'TOP_UP') {
		payload.unitPrice = totalPrice;
	} else if (type === 'PAY') {
		payload.amount = totalPrice;
	} else if (type === 'LOYALTY_SUBSCRIPTION') {
		payload.amount = totalPrice;
	}

	return payload;
}

export function makeBankPaymentPayload({
	items,
	totalPrice,
	mobileNumber,
	saveBankMuscatToken,
	createDefaultPayment,
	bankMuscatToken,
	useToken,
	email,
	name,
}: types.payment.BankPayment) {
	const payload: any = {
		amount: {
			value: totalPrice,
			unit: 'OMR',
		},
		userName: name,
		userMobileNumber: mobileNumber,
		createDefaultPayment,
		billingDetails: {
			emailAddress: email,
		},
		details: {
			items: items.map((item) => ({
				billingId: item.accountId || null,
				accountType: item.accountType || null,
				billingType: item.billingType,
				billNumber: item?.code,
				quantity: item.quantity?.toString(),
				amount: {
					value: item.unitPrice,
					unit: 'OMR',
				},
			})),
		},
		...(useToken && { useToken }),
		...(bankMuscatToken && { bankMuscatToken: { ...bankMuscatToken, saveDetails: saveBankMuscatToken } }),
	};

	return payload;
}

export function getResources() {
	return { resource: { resourceSpecification: { name: 'PHONE_NUMBER' } } };
}

export interface NewPostpaidLineData {
	creditLimit: number | string;
	phoneNumber: string;
	accountNumber: string;
	accountExternalId: string;
	subscriberNumber: string;
	simNumber: string;
	accountId: string;
	selectedOrderItems: {
		legacyProductId: string;
		place: string;
	}[];
}

export function createProductOrderForPostpaidLine(data: NewPostpaidLineData) {
	return {
		productOrder: {
			type: 'EBU',
			id: data.accountId,
			orderItems: [
				{
					action: 'ADD',
					product: {
						productCharacteristic: {
							name: 'CREDIT_LIMIT',
							value: data.creditLimit,
							action: 'ADD',
						},
					},
				},
				...data.selectedOrderItems.map((orderItem) => ({
					action: 'ADD',
					product: {
						id: orderItem.legacyProductId,
						place: orderItem.place,
					},
				})),
			],
			relatedParties: {
				customerAccount: {
					id: data.accountExternalId,
					product: {
						productCharacteristic: [
							{
								name: 'PHONE_NUMBER',
								value: data.phoneNumber,
							},
							{
								name: 'ACCOUNT_NO',
								value: data.accountNumber,
							},
							{
								name: 'ACCOUNT_EXTERNAL_ID',
								value: data.accountExternalId,
							},
							{
								name: 'SERVICE_TYPE',
								value: '1',
							},
							{
								name: 'SUBSCRIBER_NUMBER',
								value: data.subscriberNumber,
							},
							{
								name: 'SUBSCRIBER_NUMBER_RESETS',
								value: '0',
							},
							{
								name: 'SIM_NUMBER',
								value: data.simNumber,
							},
						],
					},
				},
			},
		},
	};
}

export interface NewUserData {
	username: string;
	email: string;
	userType: string;
	accountId: string;
	contactPhone1?: string;
	contactPhone2?: string;
	contactPhone3?: string;
	departmentId?: string;
	accountsIdsToManage?: string[];
	permissionExpiration?: string;
	departamentsIdsToManage?: string[];
}

export function createCorpUser(data: NewUserData) {
	return data;
}

export function getCorpUserDetails(userId: string) {
	return { userId };
}

export interface UpdateUserData {
	userId: string;
	username: string;
	email?: string;
	departmentId?: string;
	contactNumber?: string;
	accountId?: string;
	token?: string;
}

export function updateCorpUserInformation(data: UpdateUserData) {
	return data;
}

export interface UpdateUserTypeData {
	userId: string;
	userType: string; //E.g: 'ADMIN' | 'MANAGER' | 'ENDUSER' | '';
}

export function updateCorpUserType(data: UpdateUserTypeData) {
	return data;
}

export interface PermissionData {
	type: string;
	departmentId?: string;
	billingAccountId?: string;
	accountId?: string;
	permissionExpiration?: string;
}

export interface UpdateUserPermissionsData {
	userId: string;
	override?: boolean;
	permissions: PermissionData[];
}

export enum OperationContextEnum {
	CORPSUPERADMINCHANGEEMAIL = 'CORPSUPERADMINCHANGEEMAIL',
	CORPSUPERADMINCHANGEMOBILE = 'CORPSUPERADMINCHANGEMOBILE',
	NEWDATASIM = 'NEWDATASIM',
	NEWSAWAPHYSICALSIM = 'NEWSAWAPHYSICALSIM',
	NEWSAWAESIM = 'NEWSAWAESIM',
	CORPCHANGEPASSWORD = 'CORPCHANGEPASSWORD',
	CORPCHANGECREDITLIMIT = 'CORPCHANGECREDITLIMIT',
	ADDPOSTPAID = 'ADDPOSTPAID',
	ADDFIXEDLINE = 'ADDFIXEDLINE',
	REPLACEESIM = 'REPLACEESIM',
	CHANGEFBBPLAN = 'CHANGEFBBPLAN',
	ADDBANDWIDTHONDEMAND = 'ADDBANDWIDTHONDEMAND',
}

export function updateCorpUserPermissions(data: UpdateUserPermissionsData) {
	return data;
}

export function deleteCorpUsers(userIds: string[]) {
	return { userIds };
}

export interface DeleteAccountPermissionsData {
	accountId: string;
	userIds: string[];
}

export function deleteAccountPermissionsForCorpUsers(data: DeleteAccountPermissionsData) {
	return data;
}

export interface BillsHistoryData {
	billingId?: string;
	returnTotals?: boolean;
	returnAdjustments?: boolean;
	corpUser?: boolean;
	corpAccountId?: string;
	fromDate?: string;
	toDate?: string;
	wholeRange?: boolean;
}

export function getBillsHistoryForAccounts(data: BillsHistoryData) {
	return data;
}

export function getNumbersForUser(userId: string) {
	return { userId };
}

export function importUsersFromFile(base64data: string) {
	return { base64data };
}

export function addCorpUserPermissions(userId: string, permissions: PermissionData[]) {
	return {
		userId,
		permissions,
	};
}

export interface PaymentsHistoryData {
	corpAccountId?: string | null;
	fromDate?: string;
	toDate?: string;
}

export function getPaymentsHistoryForAccounts(paymentsHistoryData: PaymentsHistoryData) {
	return {
		corpUser: true,
		...paymentsHistoryData,
	};
}

export interface NumbersUsersToDeleteData {
	departmentId: string;
	numbers: string[];
	userIds: string[];
}

export function deleteNumbersAndUsersFromDepartment(data: NumbersUsersToDeleteData) {
	return data;
}

export function getPaymentHistoryRowItems(paymentRequestId: string) {
	return {
		corpUser: true,
		paymentRequestId,
	};
}

export interface EBillData {
	phoneNumber: string;
	accountType: string;
	billingAccountType: string;
}

export function getEBillInformation(data: EBillData) {
	return {
		customerAccount: {
			product: {
				id: 'E-BILL',
				productCharacteristic: {
					name: 'PHONE_NUMBER',
					value: data.phoneNumber,
				},
				productSpecification: {
					productSpecCharacteristic: {
						name: 'BILLING_TYPE',
						productSpecCharacteristicValue: {
							value: data.billingAccountType,
						},
					},
					productCategory: {
						type: data.accountType,
					},
				},
			},
		},
	};
}

export function validateResetPasswordEmail(email: string, token: string) {
	return { email, token, medium: 'EMAIL' };
}

export interface ChangePasswordData {
	oldpassword: string;
	password: string;
	password2: string;
	otp: string;
}

export function changePassword(data: ChangePasswordData) {
	return {
		...data,
	};
}

export interface ResetPasswordData {
	email: string;
	password: string;
	password2: string;
	otp: string;
}

export function resetPassword(data: ResetPasswordData) {
	return {
		...data,
		corpUser: true,
	};
}

export function getPaymentReceipt(paymentId: string) {
	return { paymentId };
}

export interface RegisterBusinessAccountData {
	companyName: string;
	authorizedPersonName: string;
	documentId: string;
	documentType: types.organizations.documentType;
	mobileNumber: string;
	email: string;
	username: string;
	rp: string;
	rpHash: string;
	userName: string;
	language: string;
	authPersonIdFile: string;
	authPersonIdFileName: string;
	idCopy1File: string;
	idCopy1FileName: string;
	idCopy2File: string;
	idCopy2FileName: string;
	crLicFile: string;
	crLicFileName: string;
	municipalityLicFile: string;
	municipalityLicFileName: string;
}

export function sendRegisterBusinessAccount(data: RegisterBusinessAccountData) {
	return data;
}

export interface UpdateOrganizationTaxId {
	taxId: string;
	accountId: string;
	taxIdCertificateFileName: string;
	taxIdCertificateFile: string;
}

export function updateOrganizationTaxId(data: UpdateOrganizationTaxId) {
	return data;
}

export interface UpdateEbillsEmailsData {
	accountId: string;
	billAccountId: string;
	emails: string[];
}

export function updateEbillEmails(data: UpdateEbillsEmailsData) {
	return data;
}

export function getCorpAccountsWithAdminsForCurrentUser() {
	return { retrieveAdminsCount: 'true' };
}

export interface SubmitServicesData {
	phoneNumber: string;
	ebuConfigProduct?: string;
	orderItems: types.services.IServiceItem[];
	documentId?: string;
}

export interface ExtendedSubmitServicesData extends SubmitServicesData {
	language: types.redux.settings.language;
}

export function submitServices(data: ExtendedSubmitServicesData) {
	const requestBody = { RequestBody: {} };
	data.orderItems.forEach((orderItem) => {
		Object.assign(requestBody.RequestBody, {
			[orderItem.action === 'ADD' ? 'OfferOrder' : 'OldOfferOrder']: {
				ProductOffer: {
					ProductList: {
						Product: [
							!!data.ebuConfigProduct && {
								ProductId: data.ebuConfigProduct,
							},
							...orderItem.productIds.map((productId) => ({
								ProductId: productId,
							})),
						].filter(Boolean),
					},
				},
				Subscriber: {
					SubscriberNo: data.phoneNumber,
					CommunicationLanguage: data.language === 'en' ? '1' : '11',
					CharacteristicValueList: data?.documentId
						? [
								{
									CharacteristicValue: {
										Name: 'documentId',
										Value: data?.documentId,
									},
								},
						  ]
						: [],
				},
			},
		});
	});
	return requestBody;
}

export interface ServiceData {
	accountId: string;
	phoneNumber: string;
	accountNumber: string;
	accountExternalId: string;
	subscriberNumber: string;
	subscriberNumberResets: string;
	orderItems: types.services.IOrderItem[];
	notificationSwap?: boolean;
	documentId?: string;
	otp: string;
}

export function updateServices(data: ServiceData) {
	const result = {
		productOrder: {
			type: 'EBU',
			id: data.accountId,
			otp: data.otp,
			orderItems: data.orderItems.map((orderItem) => ({
				action: orderItem.action,
				product: {
					id: orderItem.id,
					place: orderItem.place,
				},
			})),
			relatedParties: {
				customerAccount: {
					id: data.accountExternalId,
					product: {
						productCharacteristic: [
							{
								name: 'PHONE_NUMBER',
								value: data.phoneNumber,
							},
							{
								name: 'ACCOUNT_NO',
								value: data.accountNumber,
							},
							{
								name: 'ACCOUNT_EXTERNAL_ID',
								value: data.accountExternalId,
							},
							{
								name: 'SUBSCRIBER_NUMBER',
								value: data.subscriberNumber,
							},
							{
								name: 'SUBSCRIBER_NUMBER_RESETS',
								value: data.subscriberNumberResets,
							},
							{
								name: 'DOCUMENT_ID',
								value: data.documentId,
							},
						],
					},
				},
			},
		},
	};

	if (data?.notificationSwap) {
		result?.productOrder?.relatedParties?.customerAccount?.product?.productCharacteristic?.push({
			name: 'SWAP_NOTIFICATION',
			value: 'true',
		});
	}

	return result;
}

export function deleteAllAccountsForArborIdInitialize(accountId: string, orgAccountId: string) {
	return { accountId, orgAccountId };
}

export function deleteAllAccountsForArborIdOperation(
	accountId: string,
	orgAccountId: string,
	token?: string,
	documentId?: string
) {
	return { accountId, orgAccountId, token, documentId };
}

export interface UpdateBillingAccountInformationData {
	accountId: string;
	billingAccountName: string;
	billingId: string;
}

export function updateBillingAccountInformation(data: UpdateBillingAccountInformationData) {
	return data;
}

export function getAccountNumberInfo(accountNumber: string) {
	return { accountNumber };
}

export interface AddAllAccountsForArborIdInitializeData {
	accountId: string;
	accountName: string;
	orgAccountId: string;
}

export function addAllAccountsForArborIdInitialize(data: AddAllAccountsForArborIdInitializeData) {
	return data;
}

export interface AddBillingAccountData {
	accountId: string;
	token: string;
	orgAccountId: string;
	documentId?: string;
}

export function addAllAccountsForArborIdOperation(data: AddBillingAccountData) {
	return data;
}

export function detachNumbersFromUser(corpUserId: string, numbers: string[]) {
	return {
		corpUserId,
		numbers,
	};
}

export interface ContactUsSelfcareData {
	type: string;
	subject: string;
	message: string;
	language: 'en' | 'ar';
}

export function contactUsSelfcare(data: ContactUsSelfcareData) {
	return { ...data };
}

export interface DirectMessageData {
	offer: string;
	subject: string;
	message: string;
	name?: string;
	phone: string;
	email: string;
	language: 'en' | 'ar';
	saveContact: boolean;
	returnReference: boolean;
	sendEmailToSender: boolean;
	sendCopyToSender: boolean;
	sendSms: boolean;
}

export interface RetrieveOsmIncidentData {
	incidentId: string;
}

export function retrieveOsmIncident(data: RetrieveOsmIncidentData) {
	return { ...data };
}

export interface RetrieveOsmInteractionData {
	interactionId: string;
}

export function retrieveOsmInteraction(data: RetrieveOsmInteractionData) {
	return { ...data };
}

export function getCustomerUnpaidBillsForAccounts(billingId?: string) {
	return {
		corpUser: 'true',
		billingId,
	};
}

export function getCpnAccount(billingAccountNumber: string, phoneNumber: string) {
	return {
		type: '100',
		code: billingAccountNumber,
		msisdn: phoneNumber,
	};
}

export function detachNumberFromProfile(masterPhoneNumber: string, phoneNumber: string) {
	return {
		msisdn: phoneNumber,
		login: masterPhoneNumber,
	};
}

export function activateStaticIp(phoneNumber: string, productId: string, staticIpId: string) {
	return {
		RequestBody: {
			OfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: 'STATIC_IP_REQUEST',
							},
							{
								ProductId: staticIpId,
							},
							{
								ProductId: productId,
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
			},
		},
	};
}

export function getStaticIPOrderStatus(phoneNumber: string) {
	return {
		msisdn: phoneNumber,
		operation: 'STATIC_IP',
	};
}

export function activateCpn(phoneNumber: string, billingId: string, productId: string) {
	return {
		RequestBody: {
			OfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: productId,
							},
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: {
						SubscriberNo: billingId,
						AccessKeyType: '16',
					},
				},
			},
		},
	};
}

export function deactivateCpn(phoneNumber: string, billingId: string, productId: string, shortCode?: string) {
	return {
		RequestBody: {
			OldOfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: productId,
							},
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: [
						{
							SubscriberNo: billingId,
							AccessKeyType: '16',
						},
						shortCode && {
							SubscriberNo: shortCode,
							AccessKeyType: '17',
						},
					].filter(Boolean),
				},
			},
		},
	};
}

export function addShortCode(phoneNumber: string, shortCode: string) {
	return {
		RequestBody: {
			OfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: {
						SubscriberNo: shortCode,
						AccessKeyType: '17',
					},
				},
			},
		},
	};
}

export function removeShortCode(phoneNumber: string, shortCode: string) {
	return {
		RequestBody: {
			OldOfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: {
						SubscriberNo: shortCode,
						AccessKeyType: '17',
					},
				},
			},
		},
	};
}

export function changeShortCode(phoneNumber: string, newShortCode: string, oldShortCode: string) {
	return {
		RequestBody: {
			OldOfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: {
						SubscriberNo: oldShortCode,
						AccessKeyType: '17',
					},
				},
			},
			OfferOrder: {
				ProductOffer: {
					ProductList: {
						Product: [
							{
								ProductId: 'CPN_UPDATE',
							},
						],
					},
				},
				Subscriber: {
					SubscriberNo: phoneNumber,
				},
				MemberGroup: {
					Subscriber: {
						SubscriberNo: newShortCode,
						AccessKeyType: '17',
					},
				},
			},
		},
	};
}

export interface CreateHbbAccountData {
	accountId: string;
	operationContext: 'FBB' | 'PSTN';
	firstName: string;
	networkType: string;
	region: string;
	adslUserName?: string;
	bandwidth?: string;
	cabinet: string;
	contactNumber1: string;
	contactNumber2?: string;
	dp: string;
	emailId: string;
	exe: string;
	gisDetails: string;
	latitude: string;
	longitude: string;
	plansSelected: string;
	preferredTimeToContact: string;
	swatSOSStatus: string;
	willyat: string;
	wayNumber: string;
	buildingNumber: string;
	documents: {
		content: string;
		name: string;
	};
	idExpiryDate?: string;
	plansSelectedStr?: string;
	requestChannel?: string;
	requestType?: string;
	staffRemarks?: string;
	customerName?: string;
	customerId?: string;
}

export function createHbbAccount(data: CreateHbbAccountData) {
	return {
		idExpiryDate: moment()
			.add(1, 'month')
			.format('YYYY-MM-DD'),
		plansSelectedStr: '',
		requestChannel: 'CICM',
		requestType: '',
		staffRemarks: '',
		...data,
	};
}

export function validateAdslLogin(adslLogin: string) {
	return {
		adslLogin,
	};
}

export function changeAdslPassword(oldPassword: string, newPassword: string, adslLogin: string) {
	return {
		productOrder: {
			orderItems: {
				action: 'MODIFY',
				product: {
					id: 'INTERNET_ADSL',
					productCharacteristic: [
						{ name: 'PASSWORD', value: oldPassword, action: 'DELETE' },
						{ name: 'PASSWORD', value: newPassword, action: 'ADD' },
						{ name: 'ADSL_LOGIN', value: adslLogin },
					],
				},
			},
		},
	};
}

export function getFbbEmails(adslLogin: string) {
	return {
		customerAccount: {
			product: {
				productCharacteristic: { name: 'ADSL_LOGIN', value: adslLogin },
				productSpecification: { productCategory: { type: 'EMAIL' } },
			},
		},
	};
}

export function addFbbEmail(emailLogin: string, password: string, adslLogin: string) {
	return {
		productOrder: {
			orderItems: {
				action: 'ADD',
				product: {
					id: 'INTERNET_EMAIL',
					productCharacteristic: [
						{
							name: 'EMAIL_LOGIN',
							value: emailLogin,
						},
						{
							name: 'PASSWORD',
							value: password,
						},
						{
							name: 'ADSL_LOGIN',
							value: adslLogin,
						},
					],
				},
			},
		},
	};
}

export function modifyFbbEmailPassword(password: string, email: string) {
	return {
		productOrder: {
			orderItems: {
				action: 'MODIFY',
				product: {
					id: 'INTERNET_EMAIL',
					productCharacteristic: [
						{
							name: 'PASSWORD',
							value: '',
							action: 'DELETE',
						},
						{
							name: 'PASSWORD',
							value: password,
							action: 'ADD',
						},
						{
							name: 'EMAIL_LOGIN_NAME',
							value: email,
						},
					],
				},
			},
		},
	};
}

export function changeFixedPlan({
	partNumber,
	phoneNumber,
	accountNumber,
	accountExternalId,
	subscriberNo,
	oldPartNumber,
}: IChangeFixedPlan) {
	return {
		productOrder: {
			type: 'EBU',
			id: '2323',
			orderItems: [
				{
					action: 'REMOVE',
					product: {
						id: `OM_${oldPartNumber}`,
						place: 'OM',
					},
				},
				{
					action: 'ADD',
					product: {
						id: `OM_${partNumber}`,
						place: 'OM',
					},
				},
			],
			relatedParties: {
				customerAccount: {
					id: accountExternalId,
					product: {
						productCharacteristic: [
							{
								name: 'PHONE_NUMBER',
								value: phoneNumber,
							},
							{
								name: 'ACCOUNT_NO',
								value: accountNumber,
							},
							{
								name: 'ACCOUNT_EXTERNAL_ID',
								value: accountExternalId,
							},
							{
								name: 'SUBSCRIBER_NUMBER',
								value: subscriberNo,
							},
							{
								name: 'SUBSCRIBER_NUMBER_RESETS',
								value: '0',
							},
						],
					},
				},
			},
		},
	};
}

interface Permissions {
	accountId: string;
	type: string;
}
export interface CorpUserPermissionsData {
	userId: string;
	permissions: Permissions;
}

export interface CreateBillingRequest {
	period: string;
}
